import useGetReturnRequestAvailableCarriers from "@babackapp/private-api-client/client/use-get-return-request-available-carriers";
import type {
  CarrierDto,
  ReturnRequestDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Label } from "@babackapp/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/ui/components/radio-group";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { AlertTriangle, CalendarIcon, TruckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "@babackapp/ui/components/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { cn } from "@babackapp/ui/lib/utils";
import { CarrierName } from "./carrier/carrier-name";
import useUpdateReturnRequestCarrier from "@babackapp/private-api-client/client/use-update-return-request-carrier";

const GEODIS_CARRIER_CODE = "geodis" as const;

interface ReturnRequestCarrierSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnRequest: ReturnRequestDto;
  validationButtonLabel?: string;
  onSuccess?: (returnRequest: ReturnRequestDto) => void;
  onError?: (error: unknown) => void;
}

export default function ReturnRequestCarrierSelectionModal({
  open,
  onOpenChange,
  returnRequest,
  validationButtonLabel = "returnDetail.update.modify",
  onSuccess,
  onError,
}: ReturnRequestCarrierSelectionModalProps) {
  const t = useTranslation();
  const [selectedCarrier, setSelectedCarrier] = useState<string>("");
  const [pickupDate, setPickupDate] = useState<Date | undefined>(
    returnRequest.shipping_info.pickingTiming?.pickupTimeStart
      ? new Date(returnRequest.shipping_info.pickingTiming.pickupTimeStart)
      : undefined,
  );

  const { data: availableCarriersData } = useGetReturnRequestAvailableCarriers({
    shopId: returnRequest.shopId,
    returnRequestId: returnRequest.id,
  });

  const { mutate: updateCarrier, isPending: isUpdatingCarrier } =
    useUpdateReturnRequestCarrier({
      onSuccess: (updatedReturnRequest) => {
        onSuccess?.(updatedReturnRequest);
        onOpenChange(false);
      },
      onError: (error: unknown) => {
        onError?.(error);
      },
    });

  const handleSubmit = () => {
    if (!selectedCarrier) return;

    const carriers = (availableCarriersData?.carriers as CarrierDto[]) || [];
    const selectedCarrierData = carriers.find(
      (c: CarrierDto) => c.id === selectedCarrier,
    );
    const isGeodis = String(selectedCarrierData?.code) === GEODIS_CARRIER_CODE;

    updateCarrier({
      params: {
        path: {
          shopId: returnRequest.shopId,
          returnRequestId: returnRequest.id,
        },
      },
      body: {
        carrierId: selectedCarrier,
        ...(isGeodis &&
          pickupDate && { pickupDay: format(pickupDate, "yyyy-MM-dd") }),
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("returnDetail.rightBar.updateCarrier")}</DialogTitle>
          <DialogDescription>
            {t("returnDetail.rightBar.selectCarrierDescription")}
          </DialogDescription>
        </DialogHeader>

        {open && (
          <ReturnRequestCarrierSelectionModalContent
            returnRequest={returnRequest}
            setSelectedCarrier={setSelectedCarrier}
            selectedCarrier={selectedCarrier}
            pickupDate={pickupDate}
            setPickupDate={setPickupDate}
            availableCarriersData={availableCarriersData}
          />
        )}

        <Alert variant="warning" className="mb-1">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t("returnDetail.rightBar.carrierWarningMessage")}
            <br />
            {t("returnDetail.rightBar.carrierWarningNewLabel")}
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">{t("misc.cancel")}</Button>
          </DialogClose>
          <ButtonLoading
            isLoading={isUpdatingCarrier}
            disabled={!selectedCarrier}
            onClick={handleSubmit}
          >
            {t(validationButtonLabel as "returnDetail.update.modify")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnRequestCarrierSelectionModalContent({
  returnRequest,
  setSelectedCarrier,
  selectedCarrier,
  pickupDate,
  setPickupDate,
  availableCarriersData,
}: {
  returnRequest: ReturnRequestDto;
  setSelectedCarrier: (carrierId: string) => void;
  selectedCarrier: string;
  pickupDate?: Date;
  setPickupDate: (date: Date | undefined) => void;
  availableCarriersData?: { carriers?: CarrierDto[] };
}) {
  const t = useTranslation();
  const isLoadingCarriers = !availableCarriersData;
  const carriersError = null; // We'll handle errors in the parent component
  const availableCarriers: CarrierDto[] =
    (availableCarriersData?.carriers as CarrierDto[]) || [];
  const selectedCarrierData = availableCarriers.find(
    (c) => c.id === selectedCarrier,
  );

  useEffect(() => {
    const carrier = availableCarriers.find(
      (c) => c.code === returnRequest.shipping_info.carrier,
    );

    if (carrier) {
      setSelectedCarrier(carrier.id);
    }
  }, [availableCarriers, returnRequest]);

  return (
    <div className="">
      {isLoadingCarriers ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-muted-foreground">
            {t("misc.loading")}
          </div>
        </div>
      ) : carriersError ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-destructive">{t("misc.error")}</div>
        </div>
      ) : availableCarriers.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-muted-foreground">
            {t("returnDetail.rightBar.noCarriersAvailable")}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <div className="max-h-[220px] overflow-scroll pb-4">
              <RadioGroup
                value={selectedCarrier}
                onValueChange={setSelectedCarrier}
              >
                {availableCarriers.map((carrier: CarrierDto) => (
                  <div
                    key={carrier.id}
                    className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-muted/50"
                  >
                    <RadioGroupItem value={carrier.id} id={carrier.id} />
                    <Label
                      htmlFor={carrier.id}
                      className="flex items-center gap-2 cursor-pointer flex-1"
                    >
                      <TruckIcon className="size-4" />
                      <span className="font-medium">
                        <CarrierName carrier={carrier} />
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-background to-transparent pointer-events-none" />
          </div>

          {/* Geodis Pickup Date Picker */}
          {String(selectedCarrierData?.code) === GEODIS_CARRIER_CODE && (
            <div className="space-y-2">
              <Label htmlFor="pickup-date">
                {t("returnDetail.carrierSelection.pickupDate")}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="pickup-date"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !pickupDate && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {pickupDate
                      ? format(pickupDate, "dd/MM/yyyy")
                      : t("returnDetail.carrierSelection.selectPickupDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pickupDate}
                    onSelect={setPickupDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
